import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Package, ShoppingBag, Users, RotateCcw, Mail, Tag, TrendingUp, AlertTriangle } from 'lucide-react';
import { cardClass, euro, STATUS_LABELS } from './adminUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Kpi = ({ icon: Icon, label, value, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={cardClass}
    data-testid="kpi-card"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-foreground/50">{label}</span>
      <Icon size={16} className={accent ? 'text-gold' : 'text-foreground/40'} />
    </div>
    <p className={`font-heading text-3xl mt-3 ${accent ? 'text-gold' : 'text-foreground'}`}>{value}</p>
  </motion.div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/analytics`),
    ])
      .then(([s, a]) => {
        setStats(s.data);
        setAnalytics(a.data);
      })
      .catch((e) => console.error('dashboard:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-foreground/40 py-12">Chargement du tableau de bord…</div>;
  if (!stats) return <div className="text-foreground/40 py-12">Tableau de bord indisponible.</div>;

  const maxRev = Math.max(1, ...(analytics?.revenue_by_day || []).map((d) => d.revenue));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Chiffre d'affaires" value={euro(stats.orders.revenue)} accent />
        <Kpi icon={ShoppingBag} label="Commandes" value={stats.orders.total} />
        <Kpi icon={Users} label="Clients" value={stats.customers.total} />
        <Kpi icon={Package} label="Produits" value={stats.products.total} />
        <Kpi icon={TrendingUp} label="Panier moyen" value={euro(stats.orders.average_order_value)} />
        <Kpi icon={RotateCcw} label="Retours en cours" value={stats.returns.pending} />
        <Kpi icon={Mail} label="Abonnés newsletter" value={stats.newsletter.subscribers} />
        <Kpi icon={Tag} label="Promos actives" value={stats.promo.active} />
      </div>

      {(stats.products.out_of_stock > 0 || stats.products.low_stock > 0) && (
        <div className="flex items-center gap-2 text-sm text-gold/90 border border-gold/20 bg-gold/5 px-4 py-3">
          <AlertTriangle size={16} />
          {stats.products.out_of_stock} produit(s) en rupture · {stats.products.low_stock} en stock faible
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CA par jour */}
        <div className={cardClass}>
          <h3 className="font-heading text-xl mb-4">Chiffre d'affaires (par jour)</h3>
          {(analytics?.revenue_by_day || []).length === 0 ? (
            <p className="text-foreground/40 text-sm">Aucune vente enregistrée.</p>
          ) : (
            <div className="space-y-2">
              {analytics.revenue_by_day.slice(-10).map((d) => (
                <div key={d.date} className="flex items-center gap-3">
                  <span className="text-xs text-foreground/40 w-20">{d.date.slice(5)}</span>
                  <div className="flex-1 bg-black/5 h-4">
                    <div className="bg-gold h-4" style={{ width: `${(d.revenue / maxRev) * 100}%` }} />
                  </div>
                  <span className="text-xs text-foreground/60 w-20 text-right">{euro(d.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top produits */}
        <div className={cardClass}>
          <h3 className="font-heading text-xl mb-4">Top produits</h3>
          {(analytics?.top_products || []).length === 0 ? (
            <p className="text-foreground/40 text-sm">Aucune vente enregistrée.</p>
          ) : (
            <ul className="space-y-2">
              {analytics.top_products.slice(0, 8).map((p) => (
                <li key={p.product_id} className="flex justify-between text-sm border-b border-black/5 pb-2">
                  <span className="text-foreground/80 truncate pr-2">{p.name || p.product_id}</span>
                  <span className="text-foreground/50 whitespace-nowrap">{p.quantity} vendus · {euro(p.revenue)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Commandes par statut */}
      <div className={cardClass}>
        <h3 className="font-heading text-xl mb-4">Commandes par statut</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(stats.orders.by_status || {}).map(([st, n]) => (
            <span key={st} className="text-sm border border-black/10 px-3 py-1.5">
              <span className="text-foreground/50">{STATUS_LABELS[st] || st}</span> · <span className="text-gold">{n}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
